import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  MessageCircle, Trash2, Send, Loader2, Plus,
  Sparkles, Search, Clock, ArrowLeft, ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatHistory() {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [aiBalance, setAiBalance] = useState(null);
  const [wsId, setWsId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionError, setSessionError] = useState(null);
  const messagesEndRef = useRef(null);

  // Fetch workspace
  useEffect(() => {
    const fetchWs = async () => {
      try {
        const res = await apiFetch(`${API}/api/workspaces`);
        if (res.ok) {
          const data = await res.json();
          const ws = data.workspaces?.[0] || data[0];
          if (ws) setWsId(ws.workspace_id);
        }
      } catch (e) { /* silent */ }
    };
    fetchWs();
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Fetch AI balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await apiFetch(`${API}/api/dscr/ai-balance`);
        if (res.ok) setAiBalance(await res.json());
      } catch (e) { /* silent */ }
    };
    fetchBalance();
  }, [messages]);

  // Auto-scroll
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversation = async (convId) => {
    setMsgLoading(true);
    setActiveConv(convId);
    setSessionError(null);
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations/${convId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { /* silent */ }
    setMsgLoading(false);
  };

  const deleteConversation = async (convId, e) => {
    e?.stopPropagation();
    try {
      await apiFetch(`${API}/api/quick-chat/conversations/${convId}`, { method: 'DELETE' });
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (activeConv === convId) { setActiveConv(null); setMessages([]); }
    } catch (e) { /* silent */ }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending || !wsId) return;
    const userMsg = newMessage.trim();
    setNewMessage('');
    setSessionError(null);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await apiFetch(`${API}/api/quick-chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, message: userMsg, conversation_id: activeConv }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
        if (!activeConv && data.conversation_id) {
          setActiveConv(data.conversation_id);
          fetchConversations();
        }
      } else if (res.status === 402) {
        const err = await res.json();
        setSessionError(err.detail);
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setSending(false);
  };

  const startNewChat = () => {
    setActiveConv(null);
    setMessages([]);
    setSessionError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const filteredConvs = conversations.filter(c =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeConvData = conversations.find(c => c.conversation_id === activeConv);

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden" data-testid="chat-history-page">
      {/* Left Sidebar - Conversation List */}
      <div className="w-80 border-r border-border flex flex-col bg-card flex-shrink-0">
        {/* Header */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              CFO Buck Chats
            </h2>
            <Button onClick={startNewChat} size="sm" variant="outline" className="h-8 gap-1" data-testid="new-chat-btn">
              <Plus className="w-3.5 h-3.5" /> New
            </Button>
          </div>
          {aiBalance && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn("text-xs", aiBalance.ai_sessions_remaining > 0 ? "border-emerald-500/30 text-emerald-600" : "border-amber-500/30 text-amber-600")}>
                {aiBalance.ai_sessions_remaining} / {aiBalance.ai_sessions_monthly} sessions
              </Badge>
              {aiBalance.ai_overage_charges > 0 && (
                <span className="text-[10px] text-amber-500">+${aiBalance.ai_overage_charges.toFixed(3)} overage</span>
              )}
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="chat-search"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredConvs.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">{searchQuery ? 'No matching conversations' : 'No conversations yet'}</p>
              <p className="text-xs text-muted-foreground mt-1">Start chatting with CFO Buck!</p>
            </div>
          ) : (
            filteredConvs.map(c => (
              <div
                key={c.conversation_id}
                onClick={() => loadConversation(c.conversation_id)}
                data-testid={`conv-item-${c.conversation_id}`}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all group",
                  "hover:bg-muted",
                  activeConv === c.conversation_id ? "bg-emerald-500/10 border border-emerald-500/20" : "border border-transparent"
                )}
              >
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                  activeConv === c.conversation_id ? "bg-emerald-500/20" : "bg-muted"
                )}>
                  <MessageCircle className={cn("w-4 h-4", activeConv === c.conversation_id ? "text-emerald-600" : "text-muted-foreground")} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{c.last_message}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(c.updated_at).toLocaleDateString()}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{c.message_count} msgs</span>
                  </div>
                </div>
                <button
                  onClick={(e) => deleteConversation(c.conversation_id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all flex-shrink-0"
                  data-testid={`delete-conv-${c.conversation_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Conversation View */}
      <div className="flex-1 flex flex-col bg-background">
        {activeConv ? (
          <>
            {/* Conversation Header */}
            <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{activeConvData?.name || 'Conversation'}</p>
                  <p className="text-xs text-muted-foreground">{activeConvData?.message_count || messages.length} messages</p>
                </div>
              </div>
              <Button
                onClick={() => deleteConversation(activeConv)}
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
              {msgLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={cn("flex", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    <div className={cn(
                      "max-w-[70%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
                      msg.role === 'user'
                        ? "bg-emerald-600 text-white rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    )}>
                      {msg.content}
                      {msg.timestamp && (
                        <p className={cn("text-[10px] mt-1.5", msg.role === 'user' ? "text-emerald-200" : "text-muted-foreground")}>
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              {sessionError && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-400">
                  {sessionError}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border p-4 flex-shrink-0">
              <div className="flex items-center gap-3 max-w-4xl mx-auto">
                <input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Continue the conversation..."
                  className="flex-1 bg-muted rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-emerald-500/20 border border-border"
                  disabled={sending}
                  data-testid="fullpage-chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 px-5"
                  data-testid="fullpage-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">CFO Buck Chat History</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Select a conversation from the sidebar to review, or start a new one.
              </p>
              <Button onClick={startNewChat} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="start-new-chat-btn">
                <Plus className="w-4 h-4" /> Start New Conversation
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
